import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CaretLeft, CaretRight, ArrowLeft, PaperPlaneRight, CheckCircle, XCircle, ClockCounterClockwise, FileText, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["Pending", "Approved", "Rejected", "Forwarded to DEXT"];
const DOC_TYPES = ["Invoice", "Receipt", "Bank Statement", "Purchase Order", "Contract", "Credit Note", "Expense", "Other"];
const statusBadge = {
  "Pending": "bg-amber-50 border-amber-200 text-amber-700",
  "Approved": "bg-emerald-50 border-emerald-200 text-emerald-700",
  "Rejected": "bg-red-50 border-red-200 text-red-700",
  "Forwarded to DEXT": "bg-blue-50 border-blue-200 text-blue-700",
};

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [all, setAll] = useState([]);
  const [clients, setClients] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [activities, setActivities] = useState([]);

  const load = async () => {
    const [d, list, cl, act] = await Promise.all([
      api.get(`/documents/${id}`),
      api.get("/documents"),
      api.get("/clients"),
      api.get("/activities?limit=200"),
    ]);
    setDoc(d.data); setNote(d.data.note || "");
    setAll(list.data); setClients(cl.data);
    setActivities(act.data.filter(a => a.target === id));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const { index, prev, next } = useMemo(() => {
    const i = all.findIndex(x => x.id === id);
    return { index: i, prev: i > 0 ? all[i - 1] : null, next: i >= 0 && i < all.length - 1 ? all[i + 1] : null };
  }, [all, id]);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const r = await api.patch(`/documents/${id}`, { status });
      setDoc(r.data); toast.success(`Marked ${status}`);
      const act = await api.get("/activities?limit=200");
      setActivities(act.data.filter(a => a.target === id));
    } finally { setBusy(false); }
  };
  const setClient = async (client_id) => {
    const r = await api.patch(`/documents/${id}`, { client_id });
    setDoc(r.data); toast.success("Client linked");
  };
  const saveNote = async () => {
    const r = await api.patch(`/documents/${id}`, { note });
    setDoc(r.data); toast.success("Note saved");
  };
  const sendDext = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/documents/${id}/send-to-dext`);
      toast.success(`Sent to ${r.data.to}`); await load();
    } finally { setBusy(false); }
  };

  if (!doc) return <div className="text-slate-500">Loading…</div>;

  const client = clients.find(c => c.id === doc.client_id);

  return (
    <div className="space-y-4 -mx-8 -my-8 min-h-[calc(100vh-0px)]" data-testid="doc-detail-page">
      {/* Top action bar */}
      <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/85 border-b border-slate-200 px-6 py-3 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" data-testid="back-btn" onClick={() => navigate("/documents")} className="text-slate-700">
          <ArrowLeft size={16} className="mr-1.5" /> Back
        </Button>
        <div className="mx-2 h-6 w-px bg-slate-200" />
        <Button
          data-testid="mark-approved-btn"
          disabled={busy || doc.status === "Approved"}
          onClick={() => setStatus("Approved")}
          className="bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
        >
          <CheckCircle size={16} weight="fill" className="mr-1.5" /> Mark Approved
        </Button>
        <Button
          data-testid="mark-rejected-btn"
          disabled={busy || doc.status === "Rejected"}
          onClick={() => setStatus("Rejected")}
          variant="outline"
          className="border-red-200 text-red-700 hover:bg-red-50"
        >
          <XCircle size={16} weight="fill" className="mr-1.5" /> Reject
        </Button>
        <Button
          data-testid="mark-pending-btn"
          disabled={busy || doc.status === "Pending"}
          onClick={() => setStatus("Pending")}
          variant="outline"
          className="border-amber-200 text-amber-700 hover:bg-amber-50"
        >
          <ClockCounterClockwise size={16} className="mr-1.5" /> Set Pending
        </Button>
        <Button
          data-testid="send-dext-btn"
          disabled={busy || doc.status === "Forwarded to DEXT"}
          onClick={sendDext}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <PaperPlaneRight size={16} weight="fill" className="mr-1.5" /> {busy ? "Sending…" : "Send to DEXT"}
        </Button>

        <div className="ml-auto flex items-center gap-2">
          <Button data-testid="prev-doc-btn" variant="ghost" disabled={!prev} onClick={() => navigate(`/documents/${prev.id}`)}>
            <CaretLeft size={16} className="mr-1" /> Previous
          </Button>
          <div className="text-xs font-mono text-slate-500">{index + 1} / {all.length}</div>
          <Button data-testid="next-doc-btn" variant="ghost" disabled={!next} onClick={() => navigate(`/documents/${next.id}`)}>
            Next <CaretRight size={16} className="ml-1" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4 px-6 pb-6">
        {/* Preview panel */}
        <Card className="border border-slate-200 shadow-none rounded-md p-6 min-h-[70vh]" data-testid="doc-preview">
          {doc.file_url ? (
            <iframe src={doc.file_url} title={doc.file_name} className="w-full min-h-[70vh] rounded-md border border-slate-200" />
          ) : (
            <div className="min-h-[70vh] grid place-items-center bg-slate-50 border border-dashed border-slate-300 rounded-md p-8">
              <div className="text-center max-w-md">
                <div className="mx-auto h-16 w-16 rounded-md bg-white border border-slate-200 grid place-items-center mb-4">
                  <FileText size={30} weight="duotone" className="text-slate-500" />
                </div>
                <div className="font-[Manrope] font-bold text-xl tracking-tight text-slate-900 break-words">{doc.file_name}</div>
                <div className="mt-1 text-xs font-mono text-slate-500">{doc.size_kb ? `${doc.size_kb} KB` : "no size recorded"} · {doc.id}</div>
                <div className="mt-6 text-sm text-slate-600 leading-relaxed">
                  No file preview available. Attach a file URL to display an inline PDF/image preview here.
                </div>
                <div className="mt-4 flex gap-2 justify-center">
                  <a
                    href={doc.file_url || "#"}
                    onClick={(e) => !doc.file_url && e.preventDefault()}
                    className={`text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border ${doc.file_url ? "border-slate-300 hover:bg-white" : "border-slate-200 text-slate-400 cursor-not-allowed"}`}
                  >
                    <DownloadSimple size={14} /> Download
                  </a>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Details panel */}
        <div>
          <Card className="border border-slate-200 shadow-none rounded-md">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Document</div>
                <div className="font-[Manrope] font-bold text-lg tracking-tight break-all">{doc.file_name}</div>
              </div>
              <span data-testid="status-badge" className={`px-2 py-1 rounded-md border text-[11px] font-medium ${statusBadge[doc.status]}`}>{doc.status}</span>
            </div>
            <Tabs defaultValue="details" className="p-5">
              <TabsList className="grid grid-cols-2 mb-4">
                <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5">Document type</div>
                  <Select value={doc.document_type || "Invoice"} onValueChange={async (v) => {
                    const r = await api.patch(`/documents/${id}`, { document_type: v });
                    setDoc(r.data); toast.success("Type updated");
                  }}>
                    <SelectTrigger data-testid="detail-doctype"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5">Client</div>
                  <Select value={doc.client_id || ""} onValueChange={setClient}>
                    <SelectTrigger data-testid="detail-client"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {client && (
                    <div className="mt-1 text-xs font-mono text-slate-500 space-y-0.5">
                      {(client.company || client.email) && <div>{client.company || client.email}</div>}
                      {client.dext_email && <div data-testid="detail-dext-hint" className="text-blue-700">DEXT → {client.dext_email}</div>}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5">Status</div>
                  <Select value={doc.status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="detail-status" className={`border ${statusBadge[doc.status]}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5">File URL (optional)</div>
                  <Input data-testid="detail-file-url"
                    placeholder="https://…/invoice.pdf"
                    defaultValue={doc.file_url || ""}
                    onBlur={async (e) => {
                      const val = e.target.value.trim() || null;
                      if (val === (doc.file_url || null)) return;
                      const r = await api.patch(`/documents/${id}`, { file_url: val });
                      setDoc(r.data); toast.success("File URL updated");
                    }}
                  />
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5">Note</div>
                  <Textarea data-testid="detail-note" rows={4} value={note} onChange={e => setNote(e.target.value)} />
                  <div className="mt-2 flex justify-end">
                    <Button data-testid="save-note-btn" size="sm" onClick={saveNote}>Save note</Button>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-100 text-xs font-mono text-slate-500 space-y-1">
                  <div>ID: {doc.id}</div>
                  <div>Uploaded by: {doc.uploaded_by || "—"}</div>
                  <div>Created: {new Date(doc.created_at).toLocaleString()}</div>
                  <div>Size: {doc.size_kb ? `${doc.size_kb} KB` : "—"}</div>
                </div>
              </TabsContent>

              <TabsContent value="history">
                <div className="space-y-3">
                  {activities.length === 0 && <div className="text-sm text-slate-500">No history yet.</div>}
                  {activities.map(a => (
                    <div key={a.id} className="text-sm border-l-2 border-slate-200 pl-3">
                      <div className="font-medium">{a.action}</div>
                      <div className="text-xs font-mono text-slate-500">{a.actor} · {new Date(a.created_at).toLocaleString()}</div>
                      {a.meta && Object.keys(a.meta).length > 0 && (
                        <div className="mt-1 text-xs font-mono text-slate-500 break-all">{JSON.stringify(a.meta)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>
    </div>
  );
}
